import Nuxeo from "nuxeo";

import ConfigurationManager from "../configuration/ConfigurationManager";
import URIBuilder from "../utils/URIBuilder";
import SubmissionContants from "../constants/SubmissionConstants";
import CommonContants from "../constants/CommonConstants";
// Removed usage of request in favor of browser fetch with cookie-based auth

const NUXEO_API_PATH = "/nuxeo";

const PAGE_PROVIDER_ARCHIVED_SUBMISSION = "SubmissionArchiveAdvancedSearch";
const PAGE_PROVIDER_ARCHIVED_FILE = "FileArchiveAdvancedSearch";
const PAGE_PROVIDER_LIVE_FILE = "LiveFileSearch";
const ARCHIVE_QUERY_PARAM = `archive_submission:jobStartDate >"2006-12-31T00:00:00.000Z" AND file:content/name IS NOT NULL`;
const LIVE_QUERY_PARAM = "ecm:uuid IS NOT NULL";
const DUMMY_QUERY_PARAM = "ecm:uuid IS NOT NULL";
const ARCHIVE_QUERY_PARAM_AGGREGATION = `archive_submission:jobStartDate >"2006-12-31T00:00:00.000Z" AND file:content/name IS NOT NULL`;

class NuxeoClient {
  constructor() {
    let uri = new URIBuilder(ConfigurationManager.getProperty("NUXEO_HOST"));
    uri.port = ConfigurationManager.getProperty("NUXEO_PORT");
    uri.path = NUXEO_API_PATH;

    this._nuxeoAPIUri = uri.toString();
    this._nuxeoAPI = new Nuxeo({
      baseURL: uri.toString(),
    });
  }

  static _instance = null;

  static instance() {
    if (!NuxeoClient._instance) {
      NuxeoClient._instance = new NuxeoClient();
    }

    return NuxeoClient._instance;
  }

  getUser() {
    return this._nuxeoAPI
      .header("X-No-Basic-Header", true)
      .connect()
      .then((response) => {
        return response.user;
      })
      .catch((error) => {
        this.redirectToLogin();
      });
  }

  redirectToLogin() {
    let uri = new URIBuilder(ConfigurationManager.getProperty("WBCP_HOST"));
    uri.port = ConfigurationManager.getProperty("WBCP_PORT");
    uri.path = `/redirect/index.html`;

    uri.addParam("requestedUrl", window.location.href);

    window.location.href = uri.toString();
  }

  getArchivedSubmission(submissionUUID, properties = "*") {
    let query =
      "SELECT * " +
      "FROM SubmissionArchive " +
      `WHERE ecm:uuid = '${submissionUUID}'`;

    let params = {
      query: query,
    };

    let options = {
      headers: {
        "X-NXproperties": properties,
      },
    };

    return this._nuxeoAPI
      .operation("Repository.Query")
      .params(params)
      .execute(options)
      .then((response) => {
        if (response.entries.length === 0) {
          return null;
        }

        let submission = response.entries[0];
        let associatedFilesUUIDs = [];

        associatedFilesUUIDs = associatedFilesUUIDs.concat(
          submission.properties["archive_submission:asset"]
        );
        associatedFilesUUIDs = associatedFilesUUIDs.concat(
          submission.properties["archive_submission:jobHistory"]
        );
        associatedFilesUUIDs = associatedFilesUUIDs.concat(
          submission.properties["archive_submission:parForm"]
        );

        if (associatedFilesUUIDs.length === 0) {
          submission.associatedFiles = this._groupFiles([]);
          return submission;
        }

        return this.getFiles(associatedFilesUUIDs, properties).then((files) => {
          submission.associatedFiles = this._groupFiles(files);
          return submission;
        });
      });
  }

  getArchivedSubmissions(
    page = CommonContants.FILTER_FIRST_PAGE,
    pageSize = CommonContants.FILTER_DEFAULT_PAGE_SIZE,
    sortBy = null,
    sortOrder = "ASC",
    search = null,
    properties = "*"
  ) {
    let params = {
      pageProvider: PAGE_PROVIDER_ARCHIVED_SUBMISSION,
      pageSize: pageSize,
      currentPageIndex: page,
      sortBy: sortBy,
      sortOrder: sortBy != null ? sortOrder : null,
      namedParameters: search,
    };

    if (search !== null) {
      for (let key in search) {
        let value = search[key];
        params[key] = Array.isArray(value) ? JSON.stringify(value) : value;
      }
    }

    if (!params.queryParams || params.queryParams.length === 0) {
      params.queryParams = DUMMY_QUERY_PARAM;
    }
    let options = {
      headers: {
        "X-NXproperties": properties,
      },
    };

    return this._nuxeoAPI
      .repository()
      .query(params, options)
      .then((response) => {
        let submissions = response.entries;

        // Enrich submissions with the thumbnails from assets
        let thumbnailsIdSubmissionMap = new Map();
        for (let submission of submissions) {
          let assets = submission.properties["archive_submission:asset"];

          if (!assets || assets.length === 0) {
            continue;
          }

          thumbnailsIdSubmissionMap.set(assets[0], submission);
        }

        let thumbnailsFilesUUIDs = Array.from(thumbnailsIdSubmissionMap.keys());
        if (thumbnailsFilesUUIDs.length === 0) {
          return response;
        }

        return this.getFiles(
          thumbnailsFilesUUIDs,
          "archive_file, picture"
        ).then((files) => {
          for (let file of files) {
            let views = file.properties["picture:views"];
            if (views.length === 0) {
              continue;
            }

            let thumbnail = views[0];
            thumbnailsIdSubmissionMap.get(file.uid).thumbnail =
              thumbnail.content.data;
          }

          return response;
        });
      });
  }

  startBulkDownload(title, files, callback) {

    // return this._nuxeoAPI
    //     .operation('Blob.BulkDownload/@async')
    //     .params(params)
    //     .input(input)
    //     .execute(options)
    //     .then((response) => this._getBulkDownloadStatus(response.headers.location, callback));

    return this._bulkDownloadAPI(title, files).then((response) => {
      const statusLocation = response && response.headers
        ? response.headers.get("location")
        : null;
      if (!statusLocation) {
        window.Materialize.toast("Unable to start download.", 2000);
        callback();
        return;
      }
      this._getBulkDownloadStatus(statusLocation, callback);
    });
  }

  _getBulkDownloadStatus(statusUrl, callback) {
    var opts = {
      method: "GET",
      headers: {},
      credentials: "include",
      redirect: "manual", //DO NOT uncomment. Not to be used
    };

    /**
     * The below commeted code must not be touched. This is used for different flows, which can be used later.
     */

    fetch(statusUrl, opts)
      .then((res) => {
        if (res.status !== 200 && res.status !== 0) {
          window.Materialize.toast(
            "Download Failed, please try again...",
            2000
          );
          callback();
        } else if (res.status === 0) {
          callback();
          window.Materialize.toast("Download has started...", 2000);
          // fetch(statusUrl.replace("/status", ""), opts).then((res) => {
          //   console.log(res);
          // });
          window.open(statusUrl.replace("/status", ""), "_self");
        } else {
          setTimeout(() => {
            window.Materialize.toast("Download is in progress...", 2000);
            console.log(statusUrl);
            this._getBulkDownloadStatus(statusUrl, callback);
          }, 2000);
        }
      })
      .catch((err) => {
        console.log("ERROR : " + err.message);
        console.log(err);
      });
  }

  /**
   * DO NOT DELETE the above code
   */

  _downloadZip(key, callback) {
    // window.open(this._nuxeoAPIUri + key, "_self");
    window.open(key, "_self");

    console.log("download url for file zip: ", key);
    //window.open(key, "_self");
    window.Materialize.toast("Download has started...", 2000);
    callback();
  }

  getFile(fileUUID, properties = "*") {
    if (!fileUUID) {
      return new Promise((resolve, reject) => resolve(null));
    }

    let query = `SELECT * FROM FileArchive WHERE ecm:uuid = '${fileUUID}'`;

    let params = {
      query: query,
    };

    let options = {
      headers: {
        "X-NXproperties": properties,
        "X-NXfetch.document": "properties",
      },
    };

    return this._nuxeoAPI
      .operation("Repository.Query")
      .params(params)
      .execute(options)
      .then(
        (response) => (response.entries.length > 0 ? response.entries[0] : null)
      );
  }

  getLiveFiles(fileUUID, properties = "*") {
    if (!fileUUID) {
      return new Promise((resolve, reject) => resolve(null));
    }

    let query = `SELECT * FROM liveFile WHERE ecm:uuid = '${fileUUID}'`;

    let params = {
      query: query,
    };

    let options = {
      headers: {
        "X-NXproperties": properties,
      },
    };

    return (
      this._nuxeoAPI
        .operation("Repository.Query")
        .params(params)
        .execute(options)
        //.then((response) => response.entries.length > 0 ? response.entries[0] : null);
        .then((response) => this.addSourceforLiveAsset(response))
    );
  }

  addSourceforLiveAsset(response) {
    if (response.entries.length > 0) {
      //Adding Source Application to Live Assets
      response.entries[0].properties["live_file:sourceApplication"] =
        "STAR LABS";
      return response.entries[0];
    } else {
      return null;
    }
  }

  getFiles(filesUUIDs, properties = "*") {
    if (!filesUUIDs || filesUUIDs.length === 0) {
      return new Promise((resolve, reject) => resolve([]));
    }

    let fileUUIDsString = filesUUIDs
      .map((fileUUID) => `'${fileUUID}'`)
      .join(",");

    let query =
      "SELECT * " +
      "FROM FileArchive " +
      `WHERE ecm:uuid IN (${fileUUIDsString})`;

    let params = {
      query: query,
    };

    let options = {
      headers: {
        "X-NXproperties": properties,
      },
    };

    return this._nuxeoAPI
      .operation("Repository.Query")
      .params(params)
      .execute(options)
      .then((response) => response.entries);
  }

  getSelectedAssetFiles(id, liveProps, archivedProps, reportType) {
    let response =
      reportType === "Active"
        ? this.getLiveFiles(id, liveProps)
        : this.getFile(id, archivedProps);

    return response;
  }

  getSubmissionReportFiles(
    page = CommonContants.FILTER_FIRST_PAGE,
    pageSize = CommonContants.FILTER_DEFAULT_PAGE_SIZE,
    sortBy = null,
    sortOrder = "ASC",
    search = null,
    reportType,
    properties = "*"
  ) {
    let response =
      reportType === "Archived"
        ? this.getArchivedSubmissions(
            page,
            pageSize,
            sortBy,
            sortOrder,
            search,
            properties
          )
        : this.getArchivedSubmissions(
            page,
            pageSize,
            sortBy,
            sortOrder,
            search,
            properties
          );

    return response;
  }

  getAssetsReportFiles(
    page = CommonContants.FILTER_FIRST_PAGE,
    pageSize = CommonContants.FILTER_DEFAULT_PAGE_SIZE,
    sortBy = null,
    sortOrder = "ASC",
    search = null,
    reportType,
    properties = "*"
  ) {
    let response =
      reportType === "Active"
        ? this.getLiveSubmissionFiles(
            page,
            pageSize,
            sortBy,
            sortOrder,
            search,
            properties
          )
        : this.getArchivedFiles(
            page,
            pageSize,
            sortBy,
            sortOrder,
            search,
            properties
          );

    return response;
  }

  getArchivedFiles(
    page = CommonContants.FILTER_FIRST_PAGE,
    pageSize = CommonContants.FILTER_DEFAULT_PAGE_SIZE,
    sortBy = null,
    sortOrder = "ASC",
    search = null,
    properties = "*"
  ) {
    var count = 0;
    let params = {
      pageProvider: PAGE_PROVIDER_ARCHIVED_FILE,
      pageSize: pageSize ? pageSize : CommonContants.FILTER_DEFAULT_PAGE_SIZE,
      currentPageIndex: page ? page : CommonContants.FILTER_FIRST_PAGE,
      sortBy: sortBy,
      sortOrder: sortBy != null ? sortOrder : null,
      namedParameters: null,
    };

    if (search !== null) {
      for (let key in search) {
        let value = search[key];
        params[key] = Array.isArray(value) ? JSON.stringify(value) : value;
      }
    }

    if (!params.queryParams || params.queryParams.length === 0) {
      if (count > 10) {
        params.queryParams = ARCHIVE_QUERY_PARAM_AGGREGATION;
      } else {
        params.queryParams = ARCHIVE_QUERY_PARAM;
      }
    }
    let options = {
      headers: {
        "X-NXproperties": properties,
        "X-NXfetch.document": "properties",
      },
    };

    return this._nuxeoAPI.repository().query(params, options);
  }

  getLiveSubmissionFiles(
    page = CommonContants.FILTER_FIRST_PAGE,
    pageSize = CommonContants.FILTER_DEFAULT_PAGE_SIZE,
    sortBy = null,
    sortOrder = "ASC",
    search = null,
    properties = "*"
  ) {
    let params = {
      pageProvider: PAGE_PROVIDER_LIVE_FILE,
      pageSize: pageSize ? pageSize : CommonContants.FILTER_DEFAULT_PAGE_SIZE,
      currentPageIndex: page ? page : CommonContants.FILTER_FIRST_PAGE,
      sortBy: sortBy,
      sortOrder: sortBy != null ? sortOrder : null,
      namedParameters: null,
    };

    if (search !== null) {
      for (let key in search) {
        let value = search[key];
        params[key] = Array.isArray(value) ? JSON.stringify(value) : value;
      }
    }

    if (!params.queryParams || params.queryParams.length === 0) {
      params.queryParams = LIVE_QUERY_PARAM;
    }
    let options = {
      headers: {
        "X-NXproperties": properties,
      },
    };

    return this._nuxeoAPI.repository().query(params, options);
  }

  updateFile(fileUUID, data, type, properties = "*") {
    let options = {
      headers: {
        "X-NXproperties": properties,
      },
    };

    if (type === "FileArchive") {
      this.getFile(fileUUID).then((file) => {
        this._nuxeoAPI
          .repository()
          .fetch(file.path, options)
          .then((doc) => {
            doc.set(data);
            return doc.save();
          });
      });
    } else {
      this.getLiveFiles(fileUUID).then((file) => {
        this._nuxeoAPI
          .repository()
          .fetch(file.path, options)
          .then((doc) => {
            doc.set(data);
            return doc.save();
          });
      });
    }
  }

  getArchivedSubmissionAssociatedFiles(submissionUUID) {
    let query =
      "SELECT * " +
      "FROM FileArchive " +
      `WHERE archive_file:archiveSubmission = '${submissionUUID}'`;

    let params = {
      query: query,
    };

    let options = {
      headers: {
        "X-NXproperties": "dublincore, picture, file",
      },
    };

    return this._nuxeoAPI
      .operation("Repository.Query")
      .params(params)
      .execute(options)
      .then((response) => this._groupFiles(response.entries));
  }

  getArchivedSubmissionsAssociatedFiles(submissionsUUIDs, group = false) {
    if (!submissionsUUIDs || submissionsUUIDs.length === 0) {
      return new Promise((resolve, reject) => resolve([]));
    }

    let submissionsUUIDsString = submissionsUUIDs
      .map((submissionUUID) => `'${submissionUUID}'`)
      .join(",");

    let query =
      "SELECT * " +
      "FROM FileArchive " +
      `WHERE archive_file:archiveSubmission IN (${submissionsUUIDsString}) AND ecm:currentLifeCycleState != 'deleted'`;

    let params = {
      query: query,
    };

    let options = {
      headers: {
        "X-NXproperties": "dublincore, picture, file",
      },
    };

    return this._nuxeoAPI
      .operation("Repository.Query")
      .params(params)
      .execute(options)
      .then(
        (response) =>
          group ? this._groupFiles(response.entries) : response.entries
      );
  }

  _groupFiles(files) {
    let groupedFiles = {};

    groupedFiles["All Files"] = [];
    groupedFiles[SubmissionContants.FILE_TYPE_ASSET] = [];
    groupedFiles[SubmissionContants.FILE_TYPE_JOB_HISTORY] = [];
    groupedFiles[SubmissionContants.FILE_TYPE_PAR_FORM] = [];

    for (let i in files) {
      let fileProperties = files[i].properties;

      let type = fileProperties["archive_file:archiveClassification"];
      if (!groupedFiles[type]) {
        groupedFiles[type] = [];
      }

      let file = fileProperties["file:content"];
      if (file) {
        Object.assign(file, { uid: files[i].uid });

        file.thumbnail = fileProperties["picture:views"][
          CommonContants.FILE_VIEWS_THUMBNAIL
        ]
          ? fileProperties["picture:views"][
              CommonContants.FILE_VIEWS_THUMBNAIL
            ]["content"]
          : null;
        file.largeImage = fileProperties["picture:views"][
          CommonContants.FILE_VIEWS_ORIGINAL
        ]
          ? fileProperties["picture:views"][CommonContants.FILE_VIEWS_ORIGINAL][
              "content"
            ]
          : null;
        file.application = fileProperties["archive_file:archiveApplication"];
        file.version = fileProperties["archive_file:archiveVersionCategory"];
        file.submissionStage =
          fileProperties["archive_file:archiveSubmissionStage"];
        file.submissionDate =
          fileProperties["archive_file:archiveSubmissionDate"];
      }
      if (file === null) {
        // groupedFiles[type].push(file);
        continue;
      }
      groupedFiles["All Files"].push(file);
      groupedFiles[type].push(file);
    }

    return groupedFiles;
  }

  _requestAPI(location) {
    console.log("location: ", location);

    let options = {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
    };

    return fetch(location, options).then((response) => {
      console.log("response from status call : ", response);
      return response;
    });
  }

  _bulkDownloadAPI(title, input) {
    const url =
      this._nuxeoAPIUri.toString() +
      "/api/v1/automation/Blob.BulkDownload/@async";

    const options = {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
      body: JSON.stringify({
        params: { filename: title },
        context: {},
        input: `docs:${input.join(",")}`,
      }),
    };

    return fetch(url, options);
  }
}

export default NuxeoClient;
